import { useContext, useState, useMemo, useEffect } from 'react';
import { getWaterFallChartData } from 'components/pages/GrowthMarginDrivers/hooks/growthMarginDataUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { isEmpty, startCase } from 'lodash';
import { useGrowthMarginBridgesContext } from '../GrowthMarginBridgesContext';
import { formatNumWithKMBAlt2 } from 'utils/helperMethods';

const useDecompositionWaterfallData = () => {
    const { selectedBridgesKPI, isPercentageUnitSelected, previousYearGMBData } = useGrowthMarginBridgesContext();

    const [decompositionWaterfallData, setDecompositionWaterfallData] = useState<any[]>([]);
    const [totalMix, setTotalMix] = useState<number>(0);
    const isWaterfallChartEnabled = useMemo(() => selectedBridgesKPI === 'mix_impact', [selectedBridgesKPI]);

    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    // Get WaterFallChartData

    const {
        data: attributeData,
        isPending: isAttributeDataPending,
        isError: isAttributeDataError,
        isSuccess: isAttributeDataSuccess,
        isFetching: isAttributeDataFetching,
    } = useQuery({
        queryKey: [
            'decomposition-waterfall-infi',
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
            selectedBridgesKPI,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                kpi: 'mix_impact',
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection &&
                selectedBridgesKPI &&
                isWaterfallChartEnabled,
        ),
    });

    useEffect(() => {
        if (!isAttributeDataPending && attributeData) {
            const allData = attributeData?.data[0];
            const finalChartData = [];
            let _totalMix = 0;

            if (!isEmpty(allData)) {
                for (const [key, value] of Object.entries(allData?.kpi_values)) {
                    _totalMix += Number(value);
                    finalChartData.push({
                        kpiKey: key,
                        kpiValue: isPercentageUnitSelected ? (Number(value) / previousYearGMBData) * 100 : value,
                        attributeName: startCase(key),
                        [key]: isPercentageUnitSelected ? (Number(value) / previousYearGMBData) * 100 : value,
                        tier: allData.tier,
                    });
                }
                setDecompositionWaterfallData(finalChartData);
                setTotalMix(isPercentageUnitSelected ? (_totalMix / previousYearGMBData) * 100 : _totalMix);
            } else {
                setDecompositionWaterfallData([]);
                setTotalMix(0);
            }
        }
    }, [isAttributeDataPending, attributeData, isPercentageUnitSelected, previousYearGMBData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(
            decompositionWaterfallData,
            0,
            isPercentageUnitSelected ? (totalMix / previousYearGMBData) * 100 : totalMix,
            'kpiValue',
        );
    }, [decompositionWaterfallData, totalMix, isPercentageUnitSelected, previousYearGMBData]);

    // Prepare Excel export data from the original data (before waterfall transform)
    const excelExportData = useMemo(() => {
        if (!decompositionWaterfallData?.length) {
            return { headers: [], data: [] };
        }

        const headers = ['Name', 'Value', 'Trend'];

        const data = [];

        decompositionWaterfallData.map((item: any) => {
            data.push([
                item.attributeName || '',
                isPercentageUnitSelected
                    ? `${item.kpiValue?.toFixed(2)}%`
                    : formatNumWithKMBAlt2(item.kpiValue, 2, 2) || 0,
                Number(item.kpiValue) >= 0 ? 'positive' : 'negative',
            ]);
        });

        data.push([
            'Total Mix',
            isPercentageUnitSelected ? `${totalMix?.toFixed(2)}%` : formatNumWithKMBAlt2(totalMix, 2, 2) || 0,
            '',
            totalMix > 0 ? 'positive' : 'negative',
        ]);

        return { headers, data };
    }, [decompositionWaterfallData, isPercentageUnitSelected]);

    return {
        data: decompositionWaterfallData,
        totalMix: totalMix,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        excelExportData,
        isPending: isAttributeDataPending,
        isFetching: isAttributeDataFetching,
        isError: isAttributeDataError,
        isSuccess: isAttributeDataSuccess,
    };
};

export default useDecompositionWaterfallData;
